// Agency settings — everything that belongs to the brokerage rather
// than the signed-in user: logo, favicon, names, brand colors, bio,
// and public contact details. Per-user account stuff lives on
// /settings/profile; CRM/lead config keeps its own page (/settings).
import * as React from 'react';
import { Building2, Loader2, Palette } from 'lucide-react';
import type { AgencySettings } from '@proptr/shared';
import { useAuth } from '@proptr/api-client/react';
import { AppShell } from '../components/layout/AppShell.js';
import { ImageUploadButton } from '../components/ImageUploadButton.js';
import { SectionCard } from '../components/SectionCard.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { ColorField } from '../components/ui/color-field.js';
import { Label } from '../components/ui/label.js';
import { client } from '../main.js';

export function AgencySettingsPage() {
  const { refresh } = useAuth();
  const [settings, setSettings] = React.useState<AgencySettings | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.enterprise
      .getSettings()
      .then((s) => {
        if (alive) setSettings(s);
      })
      .catch((err: Error) => {
        if (alive) setLoadError(err.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-[860px] space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Settings
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            Your agency's branding and contact details — shared by every
            manager at the brokerage.
          </p>
        </div>

        {loadError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
            {loadError}
          </div>
        ) : settings === null ? (
          <div className="flex items-center gap-2 px-1 font-mono text-[11px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </div>
        ) : (
          <SettingsForm initial={settings} onSaved={refresh} />
        )}
      </div>
    </AppShell>
  );
}

function SettingsForm({
  initial,
  onSaved,
}: {
  initial: AgencySettings;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = React.useState<AgencySettings>(initial);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const set = <K extends keyof AgencySettings>(
    key: K,
    value: AgencySettings[K],
  ) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await client.enterprise.updateSettings(draft);
      await onSaved(); // topbar logo/name update from the session refresh
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Brand"
        subtitle="Logo, favicon, and the colors your proptrees use"
        icon={Palette}
        collapsible={false}
      >
        <div className="space-y-5 p-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <ImageField
              label="Logo"
              hint="Shown in the top bar and on your public pages."
              value={draft.logo}
              uploadKind="agency-logo"
              onChange={(v) => set('logo', v)}
              tall
            />
            <ImageField
              label="Favicon"
              hint="The small square icon browsers show in the tab."
              value={draft.favicon}
              uploadKind="agency-favicon"
              onChange={(v) => set('favicon', v)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <ColorField
              id="as-primary"
              label="Primary color"
              value={draft.primaryColor ?? ''}
              onChange={(v) => set('primaryColor', v || null)}
            />
            <ColorField
              id="as-secondary"
              label="Secondary color"
              value={draft.secondaryColor ?? ''}
              onChange={(v) => set('secondaryColor', v || null)}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Agency details"
        subtitle="Names, bio, and public contact info"
        icon={Building2}
        collapsible={false}
      >
        <div className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="as-name"
              label="Display name"
              value={draft.name}
              onChange={(v) => set('name', v)}
              placeholder="e.g. Acme Homes"
            />
            <TextField
              id="as-company"
              label="Legal / company name"
              value={draft.companyName}
              onChange={(v) => set('companyName', v)}
              placeholder="e.g. Acme Homes Real Estate LLC"
            />
          </div>
          <TextField
            id="as-website"
            label="Website"
            value={draft.companyWebsite}
            onChange={(v) => set('companyWebsite', v)}
            placeholder="https://acmehomes.com"
          />
          <div className="space-y-1.5">
            <Label htmlFor="as-bio" className="eyebrow">
              Bio
            </Label>
            <textarea
              id="as-bio"
              rows={3}
              value={draft.companyBio ?? ''}
              onChange={(e) => set('companyBio', e.target.value || null)}
              placeholder="One or two sentences about the brokerage."
              className="flex w-full resize-y rounded-md border border-border bg-card px-3.5 py-2.5 text-sm transition-colors placeholder:text-muted-foreground/70 focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:border-white/10 dark:bg-card-raised"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="as-email"
              label="Contact email"
              value={draft.email}
              onChange={(v) => set('email', v)}
              placeholder="hello@acmehomes.com"
            />
            <TextField
              id="as-phone"
              label="Contact phone"
              value={draft.phone}
              onChange={(v) => set('phone', v)}
              placeholder="+971…"
            />
          </div>
        </div>
      </SectionCard>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 font-mono text-[12px] text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && !dirty && (
          <span className="font-mono text-[11px] text-success">Saved.</span>
        )}
        <Button onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? <Loader2 className="animate-spin" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="eyebrow">
        {label}
      </Label>
      <Input
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        placeholder={placeholder}
      />
    </div>
  );
}

function ImageField({
  label,
  hint,
  value,
  uploadKind,
  onChange,
  tall = false,
}: {
  label: string;
  hint: string;
  value: string | null;
  uploadKind: 'agency-logo' | 'agency-favicon';
  onChange: (v: string | null) => void;
  tall?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <span className="eyebrow">{label}</span>
      <div className="flex items-center gap-4 rounded-md border border-border bg-card px-4 py-3 dark:border-white/10 dark:bg-card-raised">
        <div
          className={`grid shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-background ${
            tall ? 'h-14 w-24' : 'h-14 w-14'
          }`}
        >
          {value ? (
            <img
              src={value}
              alt={label}
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
              none
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {hint}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <ImageUploadButton
              kind={uploadKind}
              label={value ? 'Replace' : 'Upload'}
              onUploaded={(url) => onChange(url)}
            />
            {value && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onChange(null)}
              >
                Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Same swatch + free-text hex pattern as the onboarding brand step.
